import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API, authFetch } from "@/App";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { 
  ArrowLeft, Bell, Mail, Smartphone, Calendar, Syringe,
  FileText, Heart, AlertTriangle, Check, Send
} from "lucide-react";

export default function Notifications() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [settings, setSettings] = useState({
    email_vaccine_reminder: true,
    email_vaccine_days_before: 7,
    email_document_expiry: true,
    email_booking_confirmation: true,
    email_marketing: false,
    push_enabled: false
  });
  const [saving, setSaving] = useState(false);
  const [testEmail, setTestEmail] = useState("");
  const [sendingTest, setSendingTest] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await authFetch(`${API}/user/notification-settings`);
      if (response.ok) {
        const data = await response.json();
        setSettings(prev => ({ ...prev, ...data }));
      }
    } catch (error) {
      console.error("Error fetching settings:", error);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const response = await authFetch(`${API}/user/notification-settings`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(settings)
      });
      
      if (response.ok) {
        toast.success("Impostazioni salvate!");
      } else {
        toast.error("Errore nel salvataggio");
      }
    } catch (error) {
      toast.error("Errore di connessione");
    } finally {
      setSaving(false);
    }
  };

  const handleTestEmail = async () => {
    if (!testEmail) {
      toast.error("Inserisci un'email");
      return;
    }
    
    setSendingTest(true);
    try {
      const response = await authFetch(`${API}/notifications/test-email`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: testEmail })
      });
      
      if (response.ok) {
        toast.success("Email di test inviata!");
        setTestEmail("");
      } else {
        const error = await response.json();
        toast.error(error.detail || "Errore nell'invio");
      }
    } catch (error) {
      toast.error("Errore di connessione");
    } finally {
      setSendingTest(false);
    }
  };

  const notificationTypes = [
    {
      id: "vaccine_reminder",
      icon: Syringe,
      title: "Promemoria Vaccini",
      description: "Ricevi un reminder prima della scadenza dei vaccini",
      setting: "email_vaccine_reminder",
      color: "#FF6B4A",
      hasExtraOption: true
    },
    {
      id: "document_expiry",
      icon: FileText,
      title: "Scadenza Documenti",
      description: "Notifica quando i documenti stanno per scadere",
      setting: "email_document_expiry",
      color: "#3B82F6"
    },
    {
      id: "booking_confirmation",
      icon: Calendar,
      title: "Conferma Prenotazioni",
      description: "Ricevi conferma delle prenotazioni via email",
      setting: "email_booking_confirmation",
      color: "#10B981"
    },
    {
      id: "marketing",
      icon: Heart,
      title: "Novità e Offerte",
      description: "Aggiornamenti su nuove funzionalità e offerte partner",
      setting: "email_marketing",
      color: "#8B5CF6"
    }
  ];

  return (
    <div className="min-h-screen bg-[#F5F5F7] dark:bg-[#1D1D1F] pb-32">
      {/* Header */}
      <div className="sticky top-0 z-40 bg-white/90 dark:bg-[#1D1D1F]/90 backdrop-blur-xl border-b border-black/5 dark:border-white/10">
        <div className="max-w-2xl mx-auto px-5 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button onClick={() => navigate(-1)} className="w-10 h-10 rounded-xl bg-[#F5F5F7] dark:bg-white/10 flex items-center justify-center">
                <ArrowLeft size={20} className="text-[#1D1D1F] dark:text-white" />
              </button>
              <h1 className="text-xl font-bold text-[#1D1D1F] dark:text-white">Notifiche</h1>
            </div>
            <Button
              onClick={handleSave}
              disabled={saving}
              className="bg-[#FF6B4A] hover:bg-[#E05A3A] rounded-xl"
            >
              {saving ? "Salvataggio..." : "Salva"}
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-5 py-6 space-y-6">
        {/* Email Notifications */}
        <div className="bg-white dark:bg-white/5 rounded-2xl p-6 border border-black/5 dark:border-white/10">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-xl bg-[#FF6B4A]/10 flex items-center justify-center">
              <Mail className="text-[#FF6B4A]" size={20} />
            </div>
            <div>
              <h3 className="font-bold text-[#1D1D1F] dark:text-white">Notifiche Email</h3>
              <p className="text-sm text-[#86868B]">Scegli quali email ricevere</p>
            </div>
          </div>

          <div className="space-y-4">
            {notificationTypes.map((type) => {
              const Icon = type.icon;
              return (
                <div key={type.id} className="flex items-start justify-between py-3 border-b border-black/5 dark:border-white/10 last:border-0">
                  <div className="flex items-start gap-3">
                    <div 
                      className="w-8 h-8 rounded-lg flex items-center justify-center"
                      style={{ backgroundColor: `${type.color}15` }}
                    >
                      <Icon size={16} style={{ color: type.color }} />
                    </div>
                    <div>
                      <p className="font-medium text-[#1D1D1F] dark:text-white">{type.title}</p>
                      <p className="text-sm text-[#86868B]">{type.description}</p>
                      
                      {type.hasExtraOption && settings[type.setting] && (
                        <div className="flex items-center gap-2 mt-2">
                          <span className="text-sm text-[#86868B]">Giorni prima:</span>
                          <select
                            value={settings.email_vaccine_days_before}
                            onChange={(e) => setSettings({...settings, email_vaccine_days_before: parseInt(e.target.value)})}
                            className="px-2 py-1 rounded-lg bg-gray-100 dark:bg-white/10 text-sm"
                          >
                            <option value={3}>3 giorni</option>
                            <option value={7}>7 giorni</option>
                            <option value={14}>14 giorni</option>
                            <option value={30}>30 giorni</option>
                          </select>
                        </div>
                      )}
                    </div>
                  </div>
                  <Switch
                    checked={settings[type.setting]}
                    onCheckedChange={(checked) => setSettings({...settings, [type.setting]: checked})}
                  />
                </div>
              );
            })}
          </div>
        </div>

        {/* Test Email */}
        <div className="bg-white dark:bg-white/5 rounded-2xl p-6 border border-black/5 dark:border-white/10">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-xl bg-blue-500/10 flex items-center justify-center">
              <Send className="text-blue-500" size={20} />
            </div>
            <div>
              <h3 className="font-bold text-[#1D1D1F] dark:text-white">Test Notifiche</h3>
              <p className="text-sm text-[#86868B]">Invia un'email di prova</p>
            </div>
          </div>

          <div className="flex gap-3">
            <Input
              type="email"
              value={testEmail}
              onChange={(e) => setTestEmail(e.target.value)}
              placeholder={user?.email || "La tua email"}
              className="rounded-xl"
            />
            <Button
              onClick={handleTestEmail}
              disabled={sendingTest}
              variant="outline"
              className="rounded-xl whitespace-nowrap"
            >
              {sendingTest ? "Invio..." : "Invia Test"}
            </Button>
          </div>
        </div>

        {/* Push Notifications (coming soon) */}
        <div className="bg-white dark:bg-white/5 rounded-2xl p-6 border border-black/5 dark:border-white/10 opacity-60">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-xl bg-purple-500/10 flex items-center justify-center">
              <Smartphone className="text-purple-500" size={20} />
            </div>
            <div>
              <h3 className="font-bold text-[#1D1D1F] dark:text-white">Notifiche Push</h3>
              <p className="text-sm text-[#86868B]">Prossimamente disponibili</p>
            </div>
          </div>
          <p className="text-sm text-[#86868B]">
            Le notifiche push saranno disponibili quando scaricherai l'app mobile Zampa.
          </p>
        </div>
      </div>
    </div>
  );
}
